/**
 * Tipos y cliente del alta de negocios (US1).
 * El borrador vive en sessionStorage entre formulario y previsualización.
 */
import { api, ErrorApi } from './api'

export interface ServicioAlta {
    name: string
    duration: number
    price?: number
    color?: string
}

export interface ProfesionalAlta {
    name: string
    serviceIndexes: number[]
    color?: string
}

export interface WhatsappAlta {
    phoneNumber: string
    apiKey: string
    phoneNumberId: string
    businessAccountId: string
}

/** Forma que espera `POST /altas/validar` y `POST /altas`. */
export interface EntradaAlta {
    clinicName: string
    eaCalendarId: string
    services: ServicioAlta[]
    providers: ProfesionalAlta[]
    openDays: number[]
    openHour: number
    closeHour: number
    slotStepMinutes: number
    maxHorizonDays: number
    maxActiveBookings: number
    cancelWindowHours: number
    minLeadTimeMinutes?: number
    allowSameDayBooking?: boolean
    timezone: string
    privacyPolicyUrl: string
    consentMessage: string
    consentAcceptedMessage: string
    consentRejectedMessage: string
    address?: string
    phone?: string
    email?: string
    parking?: string
    transport?: string
    website?: string
    extraInfo?: string
    nAdministradores?: number
    whatsapp?: WhatsappAlta
    /** La mitad de la ficha que escribe una persona (012). */
    ficha: FichaAlta
    /**
     * El horario POR DÍA. `openDays`/`openHour`/`closeHour` son solo el sobre,
     * para el contrato viejo; esto es lo que se siembra en la agenda.
     */
    horarioSemanal?: Record<string, { abierto: boolean; abre: string; cierra: string }>
}

export interface ErrorCampo {
    campo: string
    motivo: string
    /** El problema escrito para una persona. Es lo que se enseña (FR-012, FR-014). */
    mensaje?: string
}

// ── La ficha del negocio (012) ────────────────────────────────────────────
//
// Es la mitad que escribe una persona. La otra —cuenta de canal, credenciales,
// calendario e identificadores de la agenda— la crea el aprovisionamiento
// después, y por eso no está aquí.

export interface ServicioFicha {
    nombre: string
    /** La duración REAL, la que se le anuncia al cliente. */
    duracionMin: number
    /**
     * Los minutos de recoger, limpiar y respirar entre una cita y la siguiente.
     * En la agenda se bloquea la SUMA de los dos; al cliente se le dice la
     * duración. Con margen cero las citas se encadenan sin descanso, así que el
     * cero también se declara.
     */
    margenMin: number
    precio?: number
    preparacion?: string
}

export interface ProfesionalFicha {
    nombre: string
    /** Posiciones dentro de `servicios`: los identificadores aún no existen. */
    serviciosIndices: number[]
}

/** Las seis responsabilidades, en los dos ejes. */
export interface MapaCapacidades {
    reservar: boolean
    modificar: boolean
    cancelar: boolean
    responder: boolean
    recordar: boolean
    escalar: boolean
}

export interface FichaAlta {
    estado: 'demo' | 'en_prueba' | 'activo' | 'pausado'
    /** Con lo que el negocio APARECE en el canal. No es la razón social. */
    nombreVisible: string
    /** Cómo NOMBRA las piezas este negocio. Explícito aunque sean los neutros. */
    vocabulario: { cliente: string; cita: string; profesional: string; servicio: string }
    /** Cómo SUENA quien las dice. Distinto del vocabulario. */
    voz: {
        tratamiento: 'tu' | 'usted' | 'espejo'
        extension: 'breve' | 'normal' | 'amplia'
        emojis: 'ninguno' | 'alguno' | 'soltura'
        presentacion: string
        idiomas: Array<'es' | 'en' | 'it'>
        /** No es estilo: es un límite. Puede ir vacío, pero tiene que estar. */
        nuncaDecir: string
    }
    capacidades: { concedidas: MapaCapacidades; activas: MapaCapacidades }
    escalada: {
        disparadores: {
            queja: boolean
            precioFueraDeLoNormal: boolean
            urgencia: boolean
            datosDeSalud: boolean
        }
        /** Entre 2 y 5. Lo acota el sistema porque quien lo pagaría mal puesto
         *  es el cliente del negocio, que no lo eligió. */
        aclaracionesAntesDeEscalar: number
    }
    /** Lo que el negocio AUTORIZA a afirmar. Fuera de aquí, el bot escala. */
    conocimiento: {
        ubicacion?: { direccion: string; indicaciones?: string } | undefined
        faq: Array<{ pregunta: string; respuesta: string }>
        telefono?: string
        /** Solo a quien entra por primera vez. Bot 072. */
        bienvenida?: string
        /** También a un cliente conocido. */
        siempre?: string
    }
    reglas: {
        antelacionMinimaReservaMin: number
        horizonteMaximoDias: number
        permiteMismoDia: boolean
        maxCitasActivasPorCliente: number
        antelacionCancelacionHoras: number
        asignacion: { criterio: 'masLiberado' | 'aleatorio' | 'fijo'; profesionalIndice?: number }
    }
    catalogo: { servicios: ServicioFicha[]; profesionales: ProfesionalFicha[] }
    /**
     * Las obligaciones legales.
     *
     * Los TEXTOS de consentimiento NO están aquí: los pone el asistente con una
     * plantilla propia y el modelo tiene prohibido redactarlos. Y que un cliente
     * ACEPTÓ tampoco: eso queda en su ficha de contacto, en Chatwoot.
     */
    legal: {
        /** Pre-rellenado con el de la plataforma y editable con el del cliente. */
        privacyPolicyUrl: string
        /** Desde cuándo rige, AAAA-MM-DD. Quien cambia la URL cambia la fecha. */
        privacyPolicyDate: string
        /** Qué decir cuando alguien cancela tarde. */
        lateCancelPolicyMsg?: string
    }
    /** Autoriza a decir los precios del catálogo. Un negocio nuevo llega en no. */
    publicarPrecios: boolean
    /** Si al devolverle una conversación el asistente ve las notas privadas. */
    leerNotasPrivadas: boolean
    /**
     * Indicaciones del negocio para el asistente.
     *
     * Pasa un filtro antes de aceptarse, pero lo que de verdad protege es dónde
     * se coloca: por debajo de las reglas de la plataforma y como preferencia,
     * nunca como orden.
     */
    instruccionesExtra?: string
    /** Spec 046: WhatsApp del dueño. Distinto del teléfono de la clínica. */
    dueno?: { telefono: string }
    /** Spec 038 · FR-009. Capa de contacto, no viaja al bot. */
    nAdministradores?: number
}

/** El de la plataforma. Se ve, y se puede cambiar por el del cliente (FR-041a). */
export const PRIVACIDAD_PLATAFORMA = 'https://aionroot.com/legal/privacidad/'
/** Desde cuándo rige esa política. Visible y editable; quien cambia la URL cambia la fecha. */
export const FECHA_POLITICA_PLATAFORMA = '2026-08-12'

export type RespuestaValidar =
    | { valido: true; previsualizacion: Record<string, unknown> }
    | { valido: false; errores: ErrorCampo[] }

export interface DetallePeticion {
    id: number
    estado: string
    eaCalendarId: string
    tenantId?: string | null
    esDemo: boolean
    recursos: Record<string, unknown>
    pasos: { orden: number; nombre: string; estado: string }[]
}

const CLAVE_BORRADOR = 'alta:borrador'
const CLAVE_PREV = 'alta:previsualizacion'

export function borradorPorDefecto(): EntradaAlta {
    return {
        clinicName: '',
        eaCalendarId: '',
        services: [{ name: '', duration: 45, price: 0 }],
        providers: [{ name: '', serviceIndexes: [0] }],
        openDays: [1, 2, 3, 4, 5],
        openHour: 9,
        closeHour: 20,
        slotStepMinutes: 15,
        maxHorizonDays: 30,
        maxActiveBookings: 2,
        cancelWindowHours: 24,
        minLeadTimeMinutes: 60,
        allowSameDayBooking: true,
        timezone: 'Atlantic/Canary',
        privacyPolicyUrl: PRIVACIDAD_PLATAFORMA,
        consentMessage: '',
        consentAcceptedMessage: 'Gracias',
        consentRejectedMessage: 'Entendido',
        ficha: fichaPorDefecto(),
    }
}

/**
 * Lo que trae el formulario puesto de salida.
 *
 * Pre-rellenar **a la vista** es correcto y deseable: el operador ve el número
 * escrito y lo cambia si quiere, y lo que acaba en la ficha es una decisión
 * suya. Lo prohibido es lo contrario — que un hueco vacío se rellene solo al
 * enviar, porque entonces la decisión deja de tener dueño (FR-015).
 *
 * Por eso el vocabulario viene con los términos neutros escritos y no en blanco:
 * el bot jamás aplica un término que no se le haya proyectado.
 */
export function fichaPorDefecto(): FichaAlta {
    const todas = (v: boolean): MapaCapacidades => ({
        reservar: v,
        modificar: v,
        cancelar: v,
        responder: v,
        recordar: v,
        escalar: v,
    })
    return {
        estado: 'demo',
        nombreVisible: '',
        vocabulario: {
            cliente: 'cliente',
            cita: 'cita',
            profesional: 'profesional',
            servicio: 'servicio',
        },
        voz: {
            tratamiento: 'usted',
            extension: 'normal',
            emojis: 'ninguno',
            presentacion: '',
            idiomas: ['es'],
            nuncaDecir: '',
        },
        capacidades: {
            concedidas: todas(true),
            // `recordar` se deja apagada: el aviso (bot 060) es una
            // decisión del negocio, no del alta. `escalar` NO se puede
            // apagar en ningún estado atendible: artículo 50 del AI Act.
            activas: { ...todas(true), recordar: false },
        },
        escalada: {
            disparadores: {
                queja: true,
                precioFueraDeLoNormal: true,
                urgencia: true,
                datosDeSalud: false,
            },
            aclaracionesAntesDeEscalar: 3,
        },
        conocimiento: { faq: [] },
        reglas: {
            antelacionMinimaReservaMin: 120,
            horizonteMaximoDias: 30,
            permiteMismoDia: true,
            maxCitasActivasPorCliente: 2,
            antelacionCancelacionHoras: 24,
            asignacion: { criterio: 'masLiberado' },
        },
        catalogo: {
            servicios: [{ nombre: '', duracionMin: 45, margenMin: 5 }],
            profesionales: [{ nombre: '', serviciosIndices: [0] }],
        },
        legal: {
            privacyPolicyUrl: PRIVACIDAD_PLATAFORMA,
            privacyPolicyDate: FECHA_POLITICA_PLATAFORMA,
            lateCancelPolicyMsg: '',
        },
        publicarPrecios: false,
        leerNotasPrivadas: false,
        instruccionesExtra: '',
    }
}

export function guardarBorrador(entrada: EntradaAlta): void {
    sessionStorage.setItem(CLAVE_BORRADOR, JSON.stringify(entrada))
}

export function leerBorrador(): EntradaAlta | null {
    const raw = sessionStorage.getItem(CLAVE_BORRADOR)
    if (!raw) return null
    try {
        return JSON.parse(raw) as EntradaAlta
    } catch {
        return null
    }
}

export function guardarPrevisualizacion(prev: Record<string, unknown>): void {
    sessionStorage.setItem(CLAVE_PREV, JSON.stringify(prev))
}

export function leerPrevisualizacion(): Record<string, unknown> | null {
    const raw = sessionStorage.getItem(CLAVE_PREV)
    if (!raw) return null
    try {
        return JSON.parse(raw) as Record<string, unknown>
    } catch {
        return null
    }
}

export function limpiarAltaLocal(): void {
    sessionStorage.removeItem(CLAVE_BORRADOR)
    sessionStorage.removeItem(CLAVE_PREV)
}

export async function validarAltaApi(entrada: EntradaAlta): Promise<RespuestaValidar> {
    return api.post<RespuestaValidar>('/altas/validar', entrada)
}

export async function importarAltaApi(contenido: string): Promise<RespuestaValidar> {
    return api.post<RespuestaValidar>('/altas/importar', { contenido })
}

export async function emitirAltaApi(
    entrada: EntradaAlta,
    idempotencyKey: string,
    esDemo: boolean,
): Promise<{ id: number; estado: 'solicitada' }> {
    return api.post('/altas', { entrada, idempotencyKey, esDemo })
}

export async function detalleAltaApi(id: number): Promise<DetallePeticion> {
    return api.get(`/altas/${id}`)
}

export function mensajeErrorApi(err: unknown): string {
    if (err instanceof ErrorApi) {
        if (err.status === 409) return 'Ese identificador de agenda o nombre ya está en uso'
        if (err.status === 404) return 'No encontrado'
        return `Error ${err.status}`
    }
    return 'No se pudo completar la operación'
}

// ═══════════════════════════════════════════════════════════════════════════════
// Borradores que sobreviven a cerrar la pestaña (US5 · FR-022…FR-028)
//
// Ojo con la distinción, que es la que se confunde: arriba hay un
// `guardarBorrador` que escribe en `sessionStorage`. **Ese sigue existiendo y
// hace otra cosa**: lleva la entrada del formulario a la pantalla de
// previsualización, y morir al cerrar la pestaña es correcto para eso.
//
// Esto de aquí es lo otro: guardar un alta a medias **en el servidor**, para
// volver mañana. Un `sessionStorage` no puede hacerlo —se pierde al cerrar, no
// se ve desde otro ordenador y no sabe que existe otra persona—, y las tres
// cosas son justo lo que pide US5.
// ═══════════════════════════════════════════════════════════════════════════════

export interface ProblemaBorrador {
    campo: string
    motivo: string
    mensaje?: string
}

export interface EstadoBorrador {
    completo: boolean
    problemas: ProblemaBorrador[]
    contratoDistinto: boolean
}

export interface BorradorResumen {
    id: number
    nombre: string
    version: number
    versionContrato: number
    actualizadoEn: string
    actualizadoPor: string | null
    estado: EstadoBorrador
}

export interface BorradorEntero extends BorradorResumen {
    datos: EntradaAlta
}

export async function listarBorradoresApi(): Promise<BorradorResumen[]> {
    const r = await api.get<{ borradores: BorradorResumen[] }>('/altas/borradores')
    return r.borradores
}

export async function leerBorradorApi(id: number): Promise<BorradorEntero> {
    return api.get<BorradorEntero>(`/altas/borradores/${id}`)
}

export async function crearBorradorApi(
    nombre: string,
    datos: EntradaAlta,
): Promise<{ id: number; version: number; estado: EstadoBorrador }> {
    return api.post('/altas/borradores', { nombre, datos })
}

/**
 * Guardar encima. `version` es la que se leyó al abrirlo.
 *
 * Si alguien lo ha tocado mientras tanto, esto lanza un `ErrorApi` con estado
 * 409 **y el cuerpo dice quién y cuándo** — que es lo que hay que enseñar. Un
 * «conflicto» a secas deja a quien lo recibe sin saber si perdió su trabajo.
 */
export async function guardarBorradorApi(
    id: number,
    datos: EntradaAlta,
    version: number,
    nombre?: string,
): Promise<{ version: number; estado: EstadoBorrador }> {
    return api.put(`/altas/borradores/${id}`, { datos, version, ...(nombre ? { nombre } : {}) })
}

export async function descartarBorradorApi(id: number): Promise<void> {
    await api.delete(`/altas/borradores/${id}`)
}
